/**
 * @module app.cd
 */

import { bindable, observable, autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from '../../shared/lib/essette-api';
import { NonNetworkMemberContainer } from 'app/um/models/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { DynamicGridEditLayoutPreviewEvent, DynamicGridReloadEvent } from 'app/shared/lib/events';
import { GridConfig, GridConfigurationService } from 'app/shared/services/grid-configuration-service';
import * as CD from 'app/cd/models/essette-api';

interface IMemberHeaderRequestParams {
    memberIncludes: string[],
    memberFields: string[],
    pcpIncludes: string[],
    pcpFields: string[],
    plansIncludes: string[],
    plansFields: string[],
    historyIncludes: string[],
    historyFields: string[],
    customIncludes: string[],
    customFields: string[]
}

@autoinject
export class MemberHeader extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable protected scope: string = '';
    @bindable protected memberId: string = '';
    @bindable protected nonMember: NonNetworkMemberContainer | null = null;
    @bindable protected memberPlan: CD.MemberPlan | null = null;

    @observable protected member: CD.Member | null = null;
    protected showMemberSummaryLink: boolean = false;

    private subscriptions: Array<Subscription> = [];

    protected openMemberSummary = openMemberSummary;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected transitional: TransitionalManager,
        protected readonly env: EnvironmentService,
        private memberService: MemberService,
        protected readonly gridConfigService: GridConfigurationService,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);

        this.checkPermissions();
    }

    protected attached(): void {
        this.subscriptions.push(this.events.subscribe(DynamicGridReloadEvent, (e: DynamicGridReloadEvent) => {
            this.load(this.memberId);
        }));

        this.subscriptions.push(this.events.subscribe(DynamicGridEditLayoutPreviewEvent, (e: DynamicGridEditLayoutPreviewEvent) => {
            this.load(this.memberId, e.gridKeys);
        }));
    }

    protected detached() {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////

    private async load(memberId: string, previewGridKeys?: string[]): Promise<void> {
        let gridKeys: string[] = [];
        // Pass the preview grid keys when editing the template
        if(!!previewGridKeys) {
            gridKeys = previewGridKeys;
        }
        // If not passing preview grid keys, load the current grid keys from the grid config service
        else {
            const config: GridConfig | undefined = await this.gridConfigService.getGridConfiguration('member-header');
            gridKeys = !!config ? config.map(cell => cell.key) : [];
        }

        this.member = await this.loadMemberHeader(memberId, gridKeys);
    }

    protected memberIdChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.load(this.memberId);
        }
    }

    protected openMemberPopup(member: CD.Member): void {
        if(member && member.id) {
            openMemberSummary(member, false);
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    // TODO: Look into converting into a utility function later for unit testing
    private buildMemberHeaderRequestParams(gridKeys: string[]): IMemberHeaderRequestParams {
        let memberIncludes: string[] = [];
        let memberFields: string[] = [];
        let pcpIncludes: string[] = [];
        let pcpFields: string[] = [];
        let plansIncludes: string[] = [];
        let plansFields: string[] = [];
        let historyIncludes: string[] = [];
        let historyFields: string[] = [];
        let customIncludes: string[] = [];
        let customFields: string[] = [];

        // Member
        if(gridKeys.indexOf('member') > -1) {
            const newIncludes = [
                'address.home',
                'address.home.region',
                'address.home.country',
                'gender',
                'language.reads',
                'language.speaks',
                'phoneNumber.home',
                'phoneNumber.home.country'
            ];
            memberIncludes = [...new Set([...memberIncludes, ...newIncludes])];
            const newFields = [
                'address.home.city',
                'address.home.postalCode',
                'address.home.region.code',
                'address.home.street1',
                'address.home.street2',
                'age.display',
                'birthDate',
                'description',
                'eligibility.isCurrent',
                'gender.description',
                'language.reads.description',
                'language.speaks.description',
                'name.first',
                'name.last',
                'name.middle',
                'phoneNumber.home.number',
                'phoneNumber.home.country.phoneNumber.*',
                'phoneNumber.home.extension'
            ];
            memberFields = [...new Set([...memberFields, ...newFields])];
        }

        // Name
        if(gridKeys.indexOf('name') > -1) {
            const newFields = [
                'description',
                'name.first',
                'name.middle',
                'name.last'
            ];
            memberFields = [...new Set([...memberFields, ...newFields])];
        }

        // Contact Info
        if(gridKeys.indexOf('contactInfo') > -1) {
            const newIncludes = [
                'phoneNumber.home',
                'phoneNumber.home.country',
                'phoneNumber.cell',
                'phoneNumber.cell.country',
                'phoneNumber.fax',
                'phoneNumber.fax.country',
                'phoneNumber.work',
                'phoneNumber.work.country'
            ];
            memberIncludes = [...new Set([...memberIncludes, ...newIncludes])];
            const newFields = [
                'emailAddress',
                'phoneNumber.home.number',
                'phoneNumber.home.country.phoneNumber.*',
                'phoneNumber.home.extension',
                'phoneNumber.cell.number',
                'phoneNumber.cell.country.phoneNumber.*',
                'phoneNumber.cell.extension',
                'phoneNumber.work.number',
                'phoneNumber.work.country.phoneNumber.*',
                'phoneNumber.work.extension',
                'phoneNumber.fax.number',
                'phoneNumber.fax.country.phoneNumber.*',
                'phoneNumber.fax.extension'
            ];
            memberFields = [...new Set([...memberFields, ...newFields])];
        }

        // Alternate Contact Info
        if(gridKeys.indexOf('alternateContactInfo') > -1) {
            const newIncludes = [
                'phoneNumbers',
                'phoneNumbers.country',
                'phoneNumbers.type'
            ];
            memberIncludes = [...new Set([...memberIncludes, ...newIncludes])];
            const newFields = [
                'phoneNumbers.number',
                'phoneNumbers.country.phoneNumber.*',
                'phoneNumbers.extension',
                'phoneNumbers.type.isFax',
                'phoneNumbers.type.description',
                'phoneNumbers.type.systemCode'
            ];
            memberFields = [...new Set([...memberFields, ...newFields])];
        }

        // PCP
        if(gridKeys.indexOf('pcp') > -1) {
            const newIncludes = [
                'primaryCarePhysician',
                'primaryCarePhysician.location',
                'primaryCarePhysician.location.facility',
                'primaryCarePhysician.location.facility.phoneNumber.primary',
                'primaryCarePhysician.location.facility.phoneNumber.primary.country'
            ];
            pcpIncludes = [...new Set([...pcpIncludes, ...newIncludes])];
            const newFields = [
                'primaryCarePhysician.name.full',
                'primaryCarePhysician.identifier.primary',
                'primaryCarePhysician.location.facility.phoneNumber.primary.number',
                'primaryCarePhysician.location.facility.phoneNumber.primary.country.phoneNumber.*',
                'primaryCarePhysician.location.facility.phoneNumber.primary.extension'
            ];
            pcpFields = [...new Set([...pcpFields, ...newFields])];
        }

        // Conditions
        if(gridKeys.indexOf('conditions') > -1) {
            const newIncludes = [
                'plans.history.conditions',
                'plans.history.conditions.condition'
            ];
            plansIncludes = [...new Set([...plansIncludes, ...newIncludes])];
            const newFields = [
                'plans.history.conditions.dateRange.start',
                'plans.history.conditions.dateRange.end',
                'plans.history.conditions.condition.description',
                'plans.history.conditions.condition.code'
            ];
            plansFields = [...new Set([...plansFields, ...newFields])];
        }

        // Aid Codes
        if(gridKeys.indexOf('aidCodes') > -1) {
            const newIncludes = [
                'plans.history.aidCodes',
                'plans.history.aidCodes.aidCode'
            ];
            plansIncludes = [...new Set([...plansIncludes, ...newIncludes])];
            const newFields = [
                'plans.history.aidCodes.dateRange.start',
                'plans.history.aidCodes.dateRange.end',
                'plans.history.aidCodes.aidCode.description',
                'plans.history.aidCodes.aidCode.code'
            ];
            plansFields = [...new Set([...plansFields, ...newFields])];
        }

        // Plans
        if(gridKeys.indexOf('plans') > -1) {
            const newIncludes = [
                'history.eligibility',
                'history.eligibility.member',
                'history.eligibility.member.plan',
                'history.eligibility.product',
                'history.eligibility.product.healthplan ',
                'history.eligibility.product.lineOfBusiness',
                'plans'
            ];
            historyIncludes = [...new Set([...historyIncludes, ...newIncludes])];
            const newFields = [
                'history.eligibility.dateRange.start',
                'history.eligibility.dateRange.end',
                'history.eligibility.member.plan.identifier.primary',
                'history.eligibility.member.plan.isPrimary',
                'history.eligibility.product.healthPlan.description',
                'history.eligibility.product.lineOfBusiness.description'
            ];
            historyFields = [...new Set([...historyFields, ...newFields])];
        }

        // Plans Product
        if(gridKeys.indexOf('plansProduct') > -1) {
            const newIncludes = [
                'history.eligibility',
                'history.eligibility.member',
                'history.eligibility.member.plan',
                'history.eligibility.product',
                'history.eligibility.product.healthplan ',
                'history.eligibility.product.lineOfBusiness',
                'plans'
            ];
            historyIncludes = [...new Set([...historyIncludes, ...newIncludes])];
            const newFields = [
                'history.eligibility.dateRange.start',
                'history.eligibility.dateRange.end',
                'history.eligibility.member.plan.identifier.primary',
                'history.eligibility.member.plan.isPrimary',
                'history.eligibility.product.description',
                'history.eligibility.product.healthPlan.description',
                'history.eligibility.product.lineOfBusiness.description'
            ];
            historyFields = [...new Set([...historyFields, ...newFields])];
        }

        // Primary Plan Custom Field Definitions
        if(gridKeys.indexOf('primaryPlanCustomFieldDefinitions') > -1) {
            const newIncludes = [
                'plans',
                'plans.custom.target.definitions'
            ];
            plansIncludes = [...new Set([...plansIncludes, ...newIncludes])];
            const newFields = [
                'plans.isPrimary',
                'plans.custom.target.definitions.code',
                'plans.custom.target.definitions.description',
                'plans.custom.text.*',
                'plans.custom.lookup.*'
            ];
            plansFields = [...new Set([...plansFields, ...newFields])];
        }

        // Plans Custom Field Definitions
        if(gridKeys.indexOf('planCustomFieldDefinitions') > -1) {
            const newIncludes = [
                'plans',
                'plans.custom.target.definitions',
                'plans.eligibility.history',
                'plans.healthPlan',
                'plans.lineOfBusiness',
            ];
            plansIncludes = [...new Set([...plansIncludes, ...newIncludes])];
            const newFields = [
                'plans.custom.target.definitions.description',
                'plans.custom.target.definitions.code',
                'plans.custom.text.*',
                'plans.custom.lookup.*',
                'plans.eligibility.history.dateRange.end',
                'plans.eligibility.rank',
                'plans.healthPlan.description',
                'plans.lineOfBusiness.description'
            ];
            plansFields = [...new Set([...plansFields, ...newFields])];
        }

        // Custom Field Definitions, also loads the same data for individual text and lookup components
        if(gridKeys.indexOf('customFieldDefinitions') > -1) {
            const newIncludes = [
                'custom',
                'custom.target.definitions',
            ];
            customIncludes = [...new Set([...customIncludes, ...newIncludes])];
            const newFields = [
                'custom.target.definitions.code',
                'custom.target.definitions.description',
                'custom.text.*',
                'custom.lookup.*'
            ];
            customFields = [...new Set([...customFields, ...newFields])];
        }
        else {
            // Text #
            if(gridKeys.filter(key => key.startsWith('text')).length > 0) {
                const newIncludes = [
                    'custom',
                    'custom.target.definitions',
                ];
                customIncludes = [...new Set([...customIncludes, ...newIncludes])];
                const newFields = [
                    'custom.target.definitions.code',
                    'custom.target.definitions.description',
                    'custom.text.*',
                ];
                customFields = [...new Set([...customFields, ...newFields])];
            }

            // Lookup #
            if(gridKeys.filter(key => key.startsWith('lookup')).length > 0) {
                const newIncludes = [
                    'custom',
                    'custom.target.definitions',
                ];
                customIncludes = [...new Set([...customIncludes, ...newIncludes])];
                const newFields = [
                    'custom.target.definitions.code',
                    'custom.target.definitions.description',
                    'custom.lookup.*'
                ];
                customFields = [...new Set([...customFields, ...newFields])];
            }
        }

        return {
            memberIncludes,
            memberFields,
            pcpIncludes,
            pcpFields,
            plansIncludes,
            plansFields,
            historyIncludes,
            historyFields,
            customIncludes,
            customFields
        };
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberHeader(memberId: string | null, gridKeys: string[]): Promise<CD.Member | null> {
        if(!memberId) return null;

        try {
            this.state.beginLoad();

            const { memberIncludes,
                memberFields,
                pcpIncludes,
                pcpFields,
                plansIncludes,
                plansFields,
                historyIncludes,
                historyFields,
                customIncludes,
                customFields } = this.buildMemberHeaderRequestParams(gridKeys);


            const [member, memberPcp, memberPlans, memberHistory, memberCustom] = await Promise.all([
                memberIncludes.length > 0 ? this.memberService.loadMember(memberId, memberIncludes, memberFields) : null,
                pcpIncludes.length > 0 ? this.memberService.loadMember(memberId, pcpIncludes, pcpFields) : null,
                plansIncludes.length > 0 ? this.memberService.loadMember(memberId, plansIncludes, plansFields) : null,
                historyIncludes.length > 0 ? this.memberService.loadMember(memberId, historyIncludes, historyFields) : null,
                customIncludes.length > 0 ? this.memberService.loadMember(memberId, customIncludes, customFields) : null
            ]);

            const returnedMember: CD.Member = member?.results[0] ?? { id: memberId };
            if(!!memberPcp) {
                returnedMember.primaryCarePhysician = memberPcp.results[0].primaryCarePhysician;
            }
            if(!!memberPlans) {
                returnedMember.plans = memberPlans.results[0].plans;
            }
            if(!!memberHistory) {
                returnedMember.history = memberHistory.results[0].history;
            }
            if(!!memberCustom) {
                returnedMember.custom = memberCustom.results[0].custom;
            }

            return returnedMember;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }

    }

}
